import React, { useState } from 'react';
import { Button, Modal, Form, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useClinics, useCreateClinic, useUpdateClinic, useDeleteClinic } from '../hooks';
import { ClinicsTable, ClinicForm } from '../components/clinics';
import { prepareClinicFormData } from '../utils/helpers';
import { CLINIC_STATUS, DEFAULT_WORKING_HOURS } from '../utils/constants';

const ClinicsTab = ({ partnerId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingClinic, setEditingClinic] = useState(null);
  const [form] = Form.useForm();

  const { data, isLoading, refetch } = useClinics(partnerId);
  const createClinic = useCreateClinic();
  const updateClinic = useUpdateClinic();
  const deleteClinic = useDeleteClinic();

  const clinics = data?.data || [];

  const handleAdd = () => {
    form.resetFields();
    form.setFieldsValue({
      status: CLINIC_STATUS.ACTIVE,
      working_hours: DEFAULT_WORKING_HOURS,
    });
    setEditingClinic(null);
    setIsModalVisible(true);
  };

  const handleEdit = (clinic) => {
    setEditingClinic(clinic);
    form.setFieldsValue({
      name: clinic.name,
      address: clinic.address,
      city: clinic.city,
      state: clinic.state,
      postal_code: clinic.postal_code,
      phone: clinic.phone,
      email: clinic.email,
      description: clinic.description,
      status: clinic.is_active ? CLINIC_STATUS.ACTIVE : CLINIC_STATUS.INACTIVE,
      working_hours: clinic.working_hours || DEFAULT_WORKING_HOURS,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (clinicId) => {
    await deleteClinic.mutateAsync({ partnerId, clinicId });
    refetch();
  };

  const handleSubmit = async (values) => {
    const formData = prepareClinicFormData(values, partnerId);

    try {
      if (editingClinic) {
        await updateClinic.mutateAsync({
          partnerId,
          clinicId: editingClinic.id,
          data: formData,
        });
      } else {
        await createClinic.mutateAsync({
          partnerId,
          data: formData,
        });
      }
      setIsModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      console.error('Error saving clinic:', error);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingClinic(null);
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Clinic
        </Button>
      </div>

      <ClinicsTable
        data={clinics}
        loading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <Modal
        title={editingClinic ? 'Edit Clinic' : 'New Clinic'}
        open={isModalVisible}
        onCancel={handleCancel}
        width={800}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <ClinicForm form={form} />

          <Space style={{ marginTop: 24, justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={createClinic.isLoading || updateClinic.isLoading}
            >
              {editingClinic ? 'Update' : 'Create'}
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default ClinicsTab;
